import { useRef, useState } from 'react';
import { copyText, selectElement } from '../lib/clipboard';
import type { PhoneJoin } from '../api/mediatorService';

interface Props {
  meetingId: string;
  /** The meeting's REAL dial-in details (sessions.metadata.phone_join).
      Replaces the old hard-coded generic Zoom numbers, which broke on
      passcode-protected meetings (Danny, 2026-08-31). */
  phoneJoin?: PhoneJoin | null;
}

type CopyStatus = 'copied' | 'failed';

/** 3-4-4 grouping so the id reads aloud cleanly: "123 4567 8901". */
const readable = (id: string) => id.replace(/\D/g, '').replace(/(\d{3})(\d{4})(\d+)/, '$1 $2 $3');

export function DialInBackup({ meetingId, phoneJoin }: Props) {
  const [statuses, setStatuses] = useState<Record<string, CopyStatus>>({});
  const valueRefs = useRef<Record<string, HTMLElement | null>>({});

  const setStatus = (key: string, status: CopyStatus, clearAfterMs: number) => {
    setStatuses((prev) => ({ ...prev, [key]: status }));
    window.setTimeout(() => {
      setStatuses((prev) => {
        if (prev[key] !== status) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, clearAfterMs);
  };

  const copy = async (value: string, key: string) => {
    const result = await copyText(value);
    if (result === 'failed') {
      const el = valueRefs.current[key];
      if (el) selectElement(el);
      setStatus(key, 'failed', 3000);
      return;
    }
    setStatus(key, 'copied', 1500);
  };

  const labelFor = (key: string): string => {
    switch (statuses[key]) {
      case 'copied':
        return 'Copied ✓';
      case 'failed':
        return '⌘C to copy';
      default:
        return 'Copy';
    }
  };

  const row = (key: string, label: string, display: string, copyValue: string) => (
    <div className="mt-3">
      <div className="text-xs font-medium text-gray-600">{label}</div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span
          ref={(el) => {
            valueRefs.current[key] = el;
          }}
          className="font-mono text-sm text-gray-800 select-all"
        >
          {display}
        </span>
        <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => copy(copyValue, key)}>
          {labelFor(key)}
        </button>
      </div>
    </div>
  );

  return (
    <section className="card p-4">
      <div className="section-label">Dial-in backup</div>
      <p className="mt-1 text-xs text-gray-500">
        Read these to a participant if they can&rsquo;t open the join link.
      </p>

      {phoneJoin ? (
        <>
          {row('number', 'Phone number', phoneJoin.number, phoneJoin.number)}
          {row('id', 'Meeting ID', readable(phoneJoin.meeting_id), phoneJoin.meeting_id.replace(/\D/g, ''))}
          {phoneJoin.passcode && row('passcode', 'Passcode', phoneJoin.passcode, phoneJoin.passcode)}
        </>
      ) : (
        <>
          {row('id', 'Meeting ID', readable(meetingId), meetingId.replace(/\D/g, ''))}
          <p className="mt-2 text-xs text-gray-500">
            Dial-in numbers for this meeting aren&rsquo;t loaded yet — they appear here within a few
            minutes of booking, or use the international list below.
          </p>
        </>
      )}

      <a
        href="https://zoom.us/zoomconference"
        target="_blank"
        rel="noreferrer"
        className="mt-3 inline-block text-xs text-brand hover:underline"
      >
        International numbers →
      </a>
    </section>
  );
}
